import { api, Profile, Job } from './api';

export interface RecommendationScore {
  contractor: Profile;
  score: number;
  reasons: string[];
  certificationCount?: number;
  insuranceCount?: number;
}

export const getContractorRecommendations = async (
  job: Job,
  limit: number = 10
): Promise<RecommendationScore[]> => {
  try {
    const params: Record<string, any> = {
      role: 'contractor',
      verification_status: 'verified',
    };

    // Note: The API may need to handle trade_type filtering differently
    // This assumes the API supports filtering by trade_type or trades array
    const contractors = await api.get<Profile[]>('/profiles', params);

    if (!contractors || contractors.length === 0) return [];

    const scoredContractors = await Promise.all(
      contractors.map(async (contractor) => {
        let score = 0;
        const reasons: string[] = [];

        if (contractor.trades?.includes(job.trade_type)) {
          score += 30;
          reasons.push(`Specializes in ${job.trade_type}`);
        } else if (contractor.trade_type === job.trade_type) {
          score += 25;
          reasons.push(`Primary trade: ${job.trade_type}`);
        }

        if (contractor.rating >= 4.5) {
          score += 20;
          reasons.push('Highly rated (4.5+)');
        } else if (contractor.rating >= 4.0) {
          score += 15;
          reasons.push('Well rated (4.0+)');
        } else if (contractor.rating >= 3.5) {
          score += 10;
        }

        if (contractor.jobs_completed >= 50) {
          score += 15;
          reasons.push('Very experienced (50+ jobs)');
        } else if (contractor.jobs_completed >= 25) {
          score += 12;
          reasons.push('Experienced (25+ jobs)');
        } else if (contractor.jobs_completed >= 10) {
          score += 8;
        } else if (contractor.jobs_completed >= 5) {
          score += 5;
        }

        if (job.location && contractor.location) {
          const jobLocation = job.location.toLowerCase();
          const contractorLocation = contractor.location.toLowerCase();

          if (contractorLocation === jobLocation) {
            score += 25;
            reasons.push('Same location');
          } else if (
            contractorLocation.includes(jobLocation.split(',')[0]) ||
            jobLocation.includes(contractorLocation.split(',')[0])
          ) {
            score += 15;
            reasons.push('Nearby location');
          }
        }

        if (job.insurance_required) {
          const insurance = await api.get('/insurance_policies', {
            user_id: contractor.id,
            status: 'active'
          });

          const insuranceCount = insurance?.length || 0;
          if (insuranceCount >= 2) {
            score += 15;
            reasons.push('Multiple active insurance policies');
          } else if (insuranceCount >= 1) {
            score += 10;
            reasons.push('Has active insurance');
          }
        }

        if (job.verification_required) {
          const certs = await api.get('/certifications', {
            user_id: contractor.id,
            verified: true
          });

          const certCount = certs?.length || 0;
          if (certCount >= 3) {
            score += 15;
            reasons.push('Multiple verified certifications');
          } else if (certCount >= 1) {
            score += 10;
            reasons.push('Has verified certifications');
          }
        }

        const previousApplications = await api.get('/applications', {
          contractor_id: contractor.id,
          job_id: job.id
        });

        if (previousApplications && previousApplications.length > 0) {
          score -= 100;
          reasons.unshift('Already applied to this job');
        }

        // Get jobs for this GC
        const gcJobs = await api.get<Job[]>('/jobs', { gc_id: job.gc_id });
        const gcJobIds = gcJobs?.map(j => j.id) || [];

        // Check if contractor has been hired for any of these jobs
        const gcApplications = await api.get('/applications', {
          contractor_id: contractor.id,
          status: 'hired',
          job_id: gcJobIds.length > 0 ? gcJobIds : undefined
        });

        const jobsWithGC = gcApplications?.filter((app: any) => 
          gcJobIds.includes(app.job_id)
        ).length || 0;

        if (jobsWithGC > 0) {
          score += 20;
          reasons.push('Previously worked with you');
        }

        const accountAge = Math.floor(
          (Date.now() - new Date(contractor.created_at).getTime()) / (1000 * 60 * 60 * 24)
        );
        if (accountAge >= 365) {
          score += 10;
          reasons.push('Established member (1+ year)');
        } else if (accountAge >= 180) {
          score += 5;
        }

        return {
          contractor,
          score,
          reasons,
        };
      })
    );

    const filtered = scoredContractors
      .filter(sc => sc.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    return filtered;
  } catch (error) {
    console.error('Error getting recommendations:', error);
    return [];
  }
};

export const getRecommendationsForGC = async (
  gcId: string,
  limit: number = 5
): Promise<RecommendationScore[]> => {
  try {
    const openJobs = await api.get<Job[]>('/jobs', {
      gc_id: gcId,
      status: 'open',
      limit: 1
    });

    if (!openJobs || openJobs.length === 0) {
      return [];
    }

    return getContractorRecommendations(openJobs[0], limit);
  } catch (error) {
    console.error('Error getting GC recommendations:', error);
    return [];
  }
};
